import { useState, type ReactNode } from 'react';
import * as RP from '@radix-ui/react-popover';
import { TriangleAlert } from 'lucide-react';
import { cn } from './cn';
import { Button } from './Button';
import { sideFor, alignFor, type Placement } from './Popover';

/**
 * Ant Design `Popconfirm` replacement, built on Radix Popover.
 *
 * ANSWERS A QUESTION, so the buttons are Yes and No. Every title here is phrased as one ("Retire
 * GATE?"), and the app's other confirmation — `useConfirmedMutation` — has always answered in those
 * words; this one defaulted to Ant's OK/Cancel, so the same decision read two different ways
 * depending on which control happened to ask it. A caller naming its own `okText` ("Yes, retire")
 * still wins, which is the better label wherever the act is worth spelling out.
 */
export function Popconfirm({
  title, description, onConfirm, onCancel, okText = 'Yes', cancelText = 'No',
  okButtonProps, disabled, placement = 'top', icon, children,
}: {
  title: ReactNode;
  description?: ReactNode;
  onConfirm?: (e?: any) => void;
  onCancel?: (e?: any) => void;
  okText?: ReactNode;
  cancelText?: ReactNode;
  okButtonProps?: { danger?: boolean; loading?: boolean };
  disabled?: boolean;
  placement?: Placement;
  icon?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  if (disabled) return <>{children}</>;
  return (
    <RP.Root open={open} onOpenChange={setOpen}>
      <RP.Trigger asChild>{children}</RP.Trigger>
      <RP.Portal>
        <RP.Content
          side={sideFor(placement)}
          align={alignFor(placement)}
          sideOffset={8}
          className="z-[var(--z-popover)] w-64 rounded-card border border-line bg-surface p-3.5 shadow-pop animate-pop-in"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex gap-2.5">
            <span className="mt-0.5 shrink-0 [&>svg]:size-4 [&>svg]:text-warning-500">{icon ?? <TriangleAlert />}</span>
            <div className="min-w-0">
              <div className="type-body-strong text-content">{title}</div>
              {description != null && <div className="mt-0.5 text-sm leading-relaxed text-muted">{description}</div>}
            </div>
          </div>
          <div className="mt-3.5 flex flex-wrap justify-end gap-2">
            <Button size="sm" variant="default" onClick={() => { setOpen(false); onCancel?.(); }}>{cancelText}</Button>
            <Button size="sm" variant={okButtonProps?.danger ? 'danger' : 'primary'} loading={okButtonProps?.loading} onClick={() => { setOpen(false); onConfirm?.(); }}>{okText}</Button>
          </div>
        </RP.Content>
      </RP.Portal>
    </RP.Root>
  );
}
